import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './Firebase';
import {
  LayoutDashboard, Users, BookOpen, CreditCard, Calendar,
  MessageSquare, Settings, LogOut, Menu, X,
  Shield, ChevronRight, Bell, Lock
} from 'lucide-react';
import AdminDashboard    from './admin/Admindashboard';
import AdminEtudiants    from './admin/Adminetudiants';
import AdminCours        from './admin/Admincours';
import AdminFinances     from './admin/Adminfinances';
import AdminCommunication from './admin/Admincommunication';
import AdminRoles        from './admin/Adminroles';
import AdminPlanning     from './admin/Adminplanning';
import AdminConnexion     from './admin/AdminConnexion';
import './admin/Admin.css';
import logo from './safi1.jpeg';
import etuImg  from './admin/etu.webp';
import etu1Img from './admin/etu1.jpg';
import etu2Img from './admin/etu2.jpg';
import etu3Img from './admin/etu3.jpg';

// ── Permissions ───────────────────────────────────────────────────────────────
export const PERMISSIONS = {
  it_manager:      ['dashboard','etudiants','cours','finances','communication','roles','planning'],
  general_manager: ['dashboard','etudiants','cours','finances','communication','roles','planning'],
  communication:   ['dashboard','etudiants','communication'],
  trainer:         ['dashboard','etudiants','cours'],
  financier:       ['dashboard','etudiants','finances'],
};

// Permissions en écriture (modification)
export const WRITE_PERMISSIONS = {
  it_manager:      ['etudiants','cours','finances','communication','roles','planning'],
  general_manager: ['etudiants','cours','finances','communication','planning'],
  communication:   ['communication'],
  trainer:         [],
  financier:       ['finances'],
};

export const ROLE_LABELS = {
  it_manager:      'IT Manager',
  general_manager: 'General Manager',
  communication:   'Manager Communication',
  trainer:         'Trainer',
  financier:       'Responsable Finances',
};

// Tous les items — tout le monde les voit
const NAV_ITEMS = [
  { key: 'dashboard',     label: 'Tableau de bord', icon: LayoutDashboard, path: '/admin' },
  { key: 'etudiants',     label: 'Étudiants',       icon: Users,           path: '/admin/etudiants' },
  { key: 'cours',         label: 'Cours & Quiz',     icon: BookOpen,        path: '/admin/cours' },
  { key: 'planning',      label: 'Planning',         icon: Calendar,        path: '/admin/planning' },
  { key: 'finances',      label: 'Finances',         icon: CreditCard,      path: '/admin/finances' },
  { key: 'communication', label: 'Communication',    icon: MessageSquare,   path: '/admin/communication' },
  { key: 'roles',         label: 'Rôles & Accès',   icon: Settings,        path: '/admin/roles' },
];

// ── Message accès refusé inline ───────────────────────────────────────────────
function AccessDenied({ section }) {
  return (
    <div className="admin-access-denied">
      <div className="admin-access-denied__icon">
        <Lock size={32}/>
      </div>
      <h3>Accès restreint</h3>
      <p>
        Vous n'avez pas les permissions nécessaires pour accéder à la section <strong>{section}</strong>.
        Contactez votre IT Manager pour obtenir l'accès.
      </p>
    </div>
  );
}

// ── Layout navbar en haut ─────────────────────────────────────────────────────
export function AdminLayout({ children, staff, onLogout }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <div className="admin-app">
      {/* ── NAVBAR ── */}
      <header className="admin-navbar">
        <div className="admin-navbar__inner">
          {/* Logo seul — clique = tableau de bord */}
          <button className="admin-navbar__brand" onClick={() => navigate('/admin')}>
            <img src={logo} alt="SAFI Academia" />
          </button>

          {/* Navigation centrale */}
          <nav className={`admin-navbar__nav ${menuOpen ? 'open' : ''}`}>
            {NAV_ITEMS.filter(item => item.key !== 'dashboard').filter(item => {
              if (item.key === 'planning') return (PERMISSIONS[staff?.role]||[]).includes('planning');
              return true;
            }).map(item => {
              const Icon = item.icon;
              const active = location.pathname === item.path ||
                (item.path !== '/admin' && location.pathname.startsWith(item.path));
              const hasAccess = (PERMISSIONS[staff?.role] || []).includes(item.key);
              return (
                <Link
                  key={item.key}
                  to={item.path}
                  className={`admin-navbar__link ${active ? 'active' : ''} ${!hasAccess ? 'locked' : ''}`}
                  onClick={() => setMenuOpen(false)}
                >
                  <Icon size={16}/>
                  <span>{item.label}</span>
                  {!hasAccess && <Lock size={11} className="admin-navbar__lock"/>}
                </Link>
              );
            })}
          </nav>

          {/* Droite — profil + déconnexion */}
          <div className="admin-navbar__right">
            <div className="admin-navbar__profile">
              <div className="admin-navbar__avatar">
                {staff?.nom?.charAt(0).toUpperCase()}
              </div>
              <div className="admin-navbar__profile-info">
                <span className="admin-navbar__profile-name">{staff?.nom?.split(' ')[0]}</span>
                <span className="admin-navbar__profile-role">{ROLE_LABELS[staff?.role]}</span>
              </div>
            </div>
            <button className="admin-navbar__logout" onClick={onLogout} title="Se déconnecter">
              <LogOut size={18}/>
            </button>
            <button className="admin-navbar__burger" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X size={22}/> : <Menu size={22}/>}
            </button>
          </div>
        </div>
      </header>

      {/* ── CONTENU ── */}
      <main className="admin-content">
        {children}
      </main>
    </div>
  );
}
// ── App principale ────────────────────────────────────────────────────────────
export default function AdminApp() {
  const navigate = useNavigate();
  const [staff, setStaff]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) { setLoading(false); return; }

      // Cherche d'abord dans la collection staff
      const staffSnap = await getDoc(doc(db, 'staff', u.uid));
      if (staffSnap.exists()) {
        const data = staffSnap.data();
        setStaff({ ...data, uid: u.uid, source: 'staff' });
        setLoading(false);
        return;
      }

      // Sinon cherche dans etudiants (pour les admins créés avant)
      const etSnap = await getDoc(doc(db, 'etudiants', u.uid));
      if (etSnap.exists()) {
        const data = etSnap.data();
        if (data.role && data.role !== 'etudiant') {
          setStaff({ ...data, uid: u.uid, source: 'etudiants' });
          setLoading(false);
          return;
        }
      }

      // Pas de compte staff trouvé
      setStaff(null);
      setLoading(false);
    });
    return unsub;
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/admin/connexion');
  };

  if (loading) return (
    <div className="admin-loading">
      <img src={logo} alt="SAFI"/>
      <div className="admin-spinner"/>
      <p>Chargement...</p>
    </div>
  );

  return (
    <Routes>
      {/* Page de connexion admin — toujours accessible */}
      <Route path="/connexion" element={<AdminConnexion/>}/>

      {/* Toutes les autres routes nécessitent d'être connecté */}
      <Route path="/*" element={
        !staff
          ? <Navigate to="/admin/connexion"/>
          : (
            <AdminLayout staff={staff} onLogout={handleLogout}>
              <Routes>
                <Route path="/" element={<AdminDashboard staff={staff}/>}/>

                <Route path="/etudiants/*" element={
                  (PERMISSIONS[staff.role]||[]).includes('etudiants')
                    ? <AdminEtudiants staff={staff}/>
                    : <AccessDenied section="Étudiants"/>
                }/>

                <Route path="/cours/*" element={
                  (PERMISSIONS[staff.role]||[]).includes('cours')
                    ? <AdminCours staff={staff}/>
                    : <AccessDenied section="Cours & Quiz"/>
                }/>

                <Route path="/planning/*" element={
                  <AdminPlanning staff={staff}/>
                }/>

                <Route path="/finances/*" element={
                  (PERMISSIONS[staff.role]||[]).includes('finances')
                    ? <AdminFinances staff={staff}/>
                    : <AccessDenied section="Finances"/>
                }/>

                <Route path="/communication/*" element={
                  (PERMISSIONS[staff.role]||[]).includes('communication')
                    ? <AdminCommunication staff={staff}/>
                    : <AccessDenied section="Communication"/>
                }/>

                <Route path="/roles/*" element={
                  (PERMISSIONS[staff.role]||[]).includes('roles')
                    ? <AdminRoles staff={staff}/>
                    : <AccessDenied section="Rôles & Accès"/>
                }/>

                <Route path="*" element={<Navigate to="/admin"/>}/>
              </Routes>
            </AdminLayout>
          )
      }/>
    </Routes>
  );
}

// Wrapper avec fond d'image pour les pages admin

export const BG_IMAGES = {
  etudiants:     etuImg,
  cours:         etu1Img,
  finances:      etu2Img,
  communication: etu3Img,
};

export function PageBg({ type, children }) {
  return (
    <div style={{position:'relative', minHeight:'100%'}}>
      <div style={{
        position:'fixed', inset:0,
        backgroundImage:`url(${BG_IMAGES[type]})`,
        backgroundSize:'cover',
        backgroundPosition:'center',
        backgroundAttachment:'fixed',
        opacity:0.07,
        pointerEvents:'none',
        zIndex:0,
      }}/>
      <div style={{position:'relative',zIndex:1}}>
        {children}
      </div>
    </div>
  );
}